// Graphs — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/backtracking.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer (grid rows, or the graph-rendering support) wherever
// it clarifies the traversal.

const graphProblems = [
  {
    id: 'number-of-islands',
    title: 'Number of Islands',
    difficulty: 'Medium',
    leetcode: { number: 200, url: 'https://leetcode.com/problems/number-of-islands/' },
    statement:
      'Given an m x n grid of \'1\' (land) and \'0\' (water), return the number of islands — a group of ' +
      'horizontally or vertically connected land cells, surrounded by water.',
    example: {
      input: 'grid = [["1","1","0","0","0"],["1","1","0","0","0"],["0","0","1","0","0"],["0","0","0","1","1"]]',
      output: '3',
      note: 'A 2×2 block top-left, a single cell in the middle, and a 1×2 block bottom-right.',
    },
    approaches: [
      {
        name: 'Approach 1 — DFS With a Separate Visited Grid',
        why:
          "Scan every cell; the moment an unvisited land cell is found, it's a new island — flood-fill outward " +
          "from it (marking every connected land cell visited) so it's never counted again. Correct, but keeping " +
          "a second grid just to track visited status costs extra memory equal to the whole board.",
        steps: [
          'Keep a separate boolean[][] visited grid, same size as the board.',
          'Scan every cell; whenever an unvisited land cell is found, increment the island count.',
          'Flood-fill outward (DFS in all 4 directions), marking every connected land cell visited — without changing the original grid.',
          'Continue scanning; already-visited cells (part of a counted island) are skipped.',
        ],
        code:
`int numIslandsBasic(char[][] grid) {
    int rows = grid.length, cols = grid[0].length;
    boolean[][] visited = new boolean[rows][cols];
    int count = 0;
    for (int r = 0; r < rows; r++) {
        for (int c = 0; c < cols; c++) {
            if (grid[r][c] == '1' && !visited[r][c]) {
                count++;
                floodFill(grid, visited, r, c);
            }
        }
    }
    return count;
}

private void floodFill(char[][] grid, boolean[][] visited, int r, int c) {
    if (r < 0 || c < 0 || r >= grid.length || c >= grid[0].length || visited[r][c] || grid[r][c] == '0') return;
    visited[r][c] = true;
    floodFill(grid, visited, r + 1, c);
    floodFill(grid, visited, r - 1, c);
    floodFill(grid, visited, r, c + 1);
    floodFill(grid, visited, r, c - 1);
}`,
        complexity: { time: 'O(rows · cols)', space: 'O(rows · cols) — the visited grid, plus recursion depth' },
        whenToUse: "A clean answer when the original grid must be preserved unmodified — otherwise, the in-place version below saves the extra grid entirely.",
      },
      {
        name: 'Approach 2 — Optimized: Sink the Island In Place',
        why:
          "Instead of a separate visited grid, mark a land cell as \"seen\" by simply overwriting it to \'0\' the " +
          "moment it's visited — sinking the island as the flood-fill explores it. No second grid needed at all.",
        steps: [
          'Scan every cell; whenever a \'1\' is found, increment the island count.',
          'Flood-fill outward (DFS in all 4 directions), overwriting every connected \'1\' to \'0\' as it\'s visited.',
          'Continue scanning; sunk cells are now \'0\' and naturally skipped by the same land-check.',
          'The grid itself doubles as the visited tracker — no extra memory beyond the recursion.',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: ['1', '1', '0', '0', '0'] },
            { label: 'row 1', values: ['1', '1', '0', '0', '0'] },
            { label: 'row 2', values: ['0', '0', '1', '0', '0'] },
            { label: 'row 3', values: ['0', '0', '0', '1', '1'] },
          ],
          varNames: ['count'],
          steps: [
            { highlights: { 'row 0': [0] }, vars: { count: 1 }, note: '(0,0) is land — island #1! Flood-fill sinks it and every connected cell.' },
            { snapshots: { 'row 0': ['0', '0', '0', '0', '0'], 'row 1': ['0', '0', '0', '0', '0'] }, vars: { count: 1 }, note: 'The whole 2×2 block (0,0),(0,1),(1,0),(1,1) is sunk to \'0\'. Scanning continues.' },
            { highlights: { 'row 2': [2] }, vars: { count: 2 }, note: '(2,2) is land, all 4 neighbors are water — island #2, a single cell. Sink it.' },
            { snapshots: { 'row 2': ['0', '0', '0', '0', '0'] }, highlights: { 'row 3': [3] }, vars: { count: 3 }, note: '(3,3) is land — island #3! Flood-fill also sinks (3,4), its connected neighbor. Final count: 3.' },
          ],
        },
        code:
`int numIslands(char[][] grid) {
    int rows = grid.length, cols = grid[0].length;
    int count = 0;
    for (int r = 0; r < rows; r++) {
        for (int c = 0; c < cols; c++) {
            if (grid[r][c] == '1') {
                count++;
                sink(grid, r, c);
            }
        }
    }
    return count;
}

private void sink(char[][] grid, int r, int c) {
    if (r < 0 || c < 0 || r >= grid.length || c >= grid[0].length || grid[r][c] == '0') return;
    grid[r][c] = '0';
    sink(grid, r + 1, c);
    sink(grid, r - 1, c);
    sink(grid, r, c + 1);
    sink(grid, r, c - 1);
}`,
        complexity: { time: 'O(rows · cols)', space: 'O(rows · cols) — recursion depth in the worst case (one giant island)' },
        whenToUse: "The default answer whenever the grid can be modified freely — the grid itself becomes the visited tracker, at zero extra memory cost beyond recursion.",
      },
    ],
  },
  {
    id: 'clone-graph',
    title: 'Clone Graph',
    difficulty: 'Medium',
    leetcode: { number: 133, url: 'https://leetcode.com/problems/clone-graph/' },
    statement: 'Given a reference node in a connected undirected graph, return a deep copy (clone) of the entire graph.',
    example: {
      input: 'adjList = [[2,4],[1,3],[2,4],[1,3]]',
      output: '[[2,4],[1,3],[2,4],[1,3]]',
      note: 'A 4-node cycle: 1-2-3-4-1. The clone must be a fully separate set of nodes with the same connections.',
    },
    approaches: [
      {
        name: 'Approach 1 — BFS With a Clone Map',
        why:
          "Because the graph can have cycles, naive recursion without remembering what's already been cloned " +
          "would loop forever. Use a HashMap from original node → its clone, and BFS outward, cloning every " +
          "node exactly once and wiring up neighbor references as they're discovered.",
        steps: [
          'Clone the starting node, and record original → clone in a HashMap.',
          'BFS outward: for each original node dequeued, walk its neighbors.',
          'If a neighbor hasn\'t been cloned yet, clone it, record it in the map, and enqueue it.',
          'Either way, add the neighbor\'s CLONE to the current node\'s clone\'s neighbor list.',
        ],
        code:
`Node cloneGraphBFS(Node start) {
    if (start == null) return null;
    Map<Node, Node> cloned = new HashMap<>();
    cloned.put(start, new Node(start.val));
    Queue<Node> queue = new LinkedList<>();
    queue.add(start);

    while (!queue.isEmpty()) {
        Node node = queue.poll();
        for (Node neighbor : node.neighbors) {
            if (!cloned.containsKey(neighbor)) {
                cloned.put(neighbor, new Node(neighbor.val));
                queue.add(neighbor);
            }
            cloned.get(node).neighbors.add(cloned.get(neighbor));
        }
    }
    return cloned.get(start);
}`,
        complexity: { time: 'O(V + E)', space: 'O(V) — the clone map, plus the queue' },
        whenToUse: "A solid default — BFS avoids any recursion-depth concern on a very large or deeply-connected graph.",
      },
      {
        name: 'Approach 2 — DFS With a Clone Map',
        why:
          "The exact same core idea — a map from original to clone is what prevents infinite loops on a cycle " +
          "— just driven by recursion instead of a queue. Whichever traversal order isn't important here; the " +
          "map is doing the real work in both versions.",
        steps: [
          'If the current node is already in the clone map, return its existing clone immediately (this is what stops infinite recursion on a cycle).',
          'Otherwise, create a clone, record it in the map BEFORE recursing (critical — see below).',
          'Recursively clone every neighbor, appending each result to the current clone\'s neighbor list.',
          'Return the clone.',
        ],
        visual: {
          graph: { label: 'original graph', nodes: [1, 2, 3, 4], edges: [[1, 2], [2, 3], [3, 4], [4, 1]], directed: false },
          varNames: [],
          steps: [
            { highlights: { graph: [1] }, note: 'Clone node 1. Record it in the map BEFORE recursing into its neighbors.' },
            { highlights: { graph: [2] }, note: 'Recurse into neighbor 2: not yet cloned — clone it, record it, recurse into ITS neighbors (1 and 3).' },
            { highlights: { graph: [1] }, note: 'Neighbor 1 of node 2: already in the map (from step 1) — return the existing clone immediately. No infinite loop.' },
            { highlights: { graph: [3, 4] }, note: 'Continue similarly: 3 gets cloned (recursing into 2 — already cloned — and 4), then 4 gets cloned (recursing into 3 — already cloned — and 1 — already cloned). Every node cloned exactly once.' },
          ],
        },
        code:
`Map<Node, Node> cloned = new HashMap<>();

Node cloneGraph(Node node) {
    if (node == null) return null;
    if (cloned.containsKey(node)) return cloned.get(node);

    Node clone = new Node(node.val);
    cloned.put(node, clone);   // record BEFORE recursing — this is what breaks the cycle
    for (Node neighbor : node.neighbors) {
        clone.neighbors.add(cloneGraph(neighbor));
    }
    return clone;
}`,
        complexity: { time: 'O(V + E)', space: 'O(V) — the clone map, plus recursion depth' },
        interviewNote: "The order matters: recording the clone in the map BEFORE recursing into neighbors is what prevents infinite recursion when a neighbor's own neighbor list eventually points back to the current node.",
        whenToUse: "Either BFS or DFS is a fully correct, equally efficient answer — pick whichever traversal style is more natural to write quickly under interview pressure.",
      },
    ],
  },
  {
    id: 'course-schedule',
    title: 'Course Schedule',
    difficulty: 'Medium',
    leetcode: { number: 207, url: 'https://leetcode.com/problems/course-schedule/' },
    statement:
      'There are numCourses courses labeled 0 to numCourses-1. prerequisites[i] = [a, b] means course b must be ' +
      'taken before course a. Return true if it\'s possible to finish all courses (i.e., no cyclic dependency).',
    example: {
      input: 'numCourses = 4, prerequisites = [[1,0],[2,0],[3,1],[3,2]]',
      output: 'true',
      note: 'Course 0 first, then 1 and 2 (in either order), then 3 — no cycle.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Try Every Ordering)',
        why:
          "Generate every possible ordering of the courses, and check whether any of them respects every " +
          "prerequisite constraint. Correct, but the number of possible orderings explodes factorially.",
        steps: [
          'Generate every permutation of the numCourses courses.',
          'For each ordering, check every prerequisite pair — does the prerequisite course appear before the course that needs it?',
          'If any ordering satisfies every constraint, return true.',
          'n! orderings, each requiring a full constraint check — wildly impractical past a handful of courses.',
        ],
        code:
`boolean canFinishBruteForce(int numCourses, int[][] prerequisites) {
    int[] courses = new int[numCourses];
    for (int i = 0; i < numCourses; i++) courses[i] = i;
    return tryPermutations(courses, 0, prerequisites);
}

private boolean tryPermutations(int[] courses, int k, int[][] prerequisites) {
    if (k == courses.length) return respectsAllPrerequisites(courses, prerequisites);
    for (int i = k; i < courses.length; i++) {
        swap(courses, k, i);
        if (tryPermutations(courses, k + 1, prerequisites)) return true;
        swap(courses, k, i);
    }
    return false;
}
// respectsAllPrerequisites(...) and swap(...) omitted for brevity.`,
        complexity: { time: 'O(n! · E)', space: 'O(n)' },
        whenToUse: "Only useful to confirm what a valid course order means — this brute force is impractical past a tiny number of courses.",
      },
      {
        name: 'Approach 2 — Optimized: Kahn\'s Algorithm (Topological Sort by In-Degree)',
        why:
          "Track how many prerequisites each course still has outstanding (its in-degree). A course with " +
          "in-degree 0 can be taken right now — take it, and decrement the in-degree of everything that depended " +
          "on it. If every course eventually reaches in-degree 0 and gets processed, there's no cycle; if some " +
          "courses are left stuck with unresolved prerequisites, a cycle is blocking them forever.",
        steps: [
          'Build an adjacency list AND an in-degree count for every course (how many prerequisites it has).',
          'Start a queue with every course that already has in-degree 0 — no prerequisites needed.',
          'Process the queue: for each course taken, decrement the in-degree of every course that depends on it. If any drops to 0, enqueue it.',
          'If every course was eventually processed, all can be finished. If some remain stuck (never reached 0), a cycle exists.',
        ],
        visual: {
          graph: { label: 'prerequisites (b → a means b must come before a)', nodes: [0, 1, 2, 3], edges: [[0, 1], [0, 2], [1, 3], [2, 3]], directed: true },
          varNames: ['queue', 'processed'],
          steps: [
            { highlights: { graph: [0] }, vars: { queue: '[0]', processed: 0 }, note: 'In-degrees: 0→0, 1→1, 2→1, 3→2. Only course 0 starts at in-degree 0 — queue: [0].' },
            { highlights: { graph: [1, 2] }, vars: { queue: '[1, 2]', processed: 1 }, note: 'Process 0: decrement 1 and 2\'s in-degree (both drop to 0) — enqueue both. Processed: 1.' },
            { highlights: { graph: [3] }, vars: { queue: '[3]', processed: 3 }, note: 'Process 1: decrement 3\'s in-degree to 1 (not 0 yet). Process 2: decrement 3\'s in-degree to 0 — enqueue it. Processed: 3.' },
            { vars: { queue: '[]', processed: 4 }, note: 'Process 3: no courses depend on it. Queue empty. All 4 courses processed — no cycle. Return true.' },
          ],
        },
        code:
`boolean canFinish(int numCourses, int[][] prerequisites) {
    Map<Integer, List<Integer>> adj = new HashMap<>();
    int[] inDegree = new int[numCourses];
    for (int i = 0; i < numCourses; i++) adj.put(i, new ArrayList<>());
    for (int[] p : prerequisites) {
        adj.get(p[1]).add(p[0]);   // p[1] (prereq) -> p[0] (course)
        inDegree[p[0]]++;
    }

    Queue<Integer> queue = new LinkedList<>();
    for (int i = 0; i < numCourses; i++) {
        if (inDegree[i] == 0) queue.add(i);
    }

    int processed = 0;
    while (!queue.isEmpty()) {
        int course = queue.poll();
        processed++;
        for (int next : adj.get(course)) {
            if (--inDegree[next] == 0) queue.add(next);
        }
    }
    return processed == numCourses;
}`,
        complexity: { time: 'O(V + E)', space: 'O(V + E)' },
        interviewNote: "Kahn's algorithm reports MORE than just true/false almost for free — the order courses get dequeued in IS a valid course schedule, which is exactly what LeetCode 210 (Course Schedule II) asks for directly.",
        whenToUse: "This is the standard answer for cycle detection in a directed graph when a genuine topological order is also useful to have — the DFS-based three-state approach (covered in this pattern's drills) is an equally valid alternative when recursion is preferred.",
      },
    ],
  },
  {
    id: 'pacific-atlantic-water-flow',
    title: 'Pacific Atlantic Water Flow',
    difficulty: 'Medium',
    leetcode: { number: 417, url: 'https://leetcode.com/problems/pacific-atlantic-water-flow/' },
    statement:
      'Given an m x n grid of heights, the Pacific Ocean touches the top and left edges, and the Atlantic Ocean ' +
      'touches the bottom and right edges. Water flows from a cell to an adjacent cell with height ≤ its own. ' +
      'Return every cell from which water can reach BOTH oceans.',
    example: {
      input: 'heights = [[1,2,2,3,5],[3,2,3,4,4],[2,4,5,3,1],[6,7,1,4,5],[5,1,1,2,4]]',
      output: '[[0,4],[1,3],[1,4],[2,2],[3,0],[3,1],[4,0]]',
      note: 'Every listed cell has a downhill (or flat) path to both the Pacific and Atlantic borders.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Simulate Flow From Every Cell)',
        why:
          "For every single cell, run a full BFS/DFS simulating water flowing outward from it, checking whether " +
          "that flow eventually reaches the Pacific border AND, separately, the Atlantic border. Correct, but " +
          "every one of the (rows × cols) cells triggers its own full grid traversal.",
        steps: [
          'For each cell, run a BFS/DFS following the flow rule (move to a neighbor with height ≤ current).',
          'Check whether that exploration reaches any Pacific-border cell.',
          'Separately, check whether it reaches any Atlantic-border cell.',
          'If both are true, record this cell — but every cell paid for its own full grid exploration.',
        ],
        code:
`List<int[]> pacificAtlanticBruteForce(int[][] heights) {
    int rows = heights.length, cols = heights[0].length;
    List<int[]> result = new ArrayList<>();
    for (int r = 0; r < rows; r++) {
        for (int c = 0; c < cols; c++) {
            if (canReach(heights, r, c, true) && canReach(heights, r, c, false)) {
                result.add(new int[] { r, c });
            }
        }
    }
    return result;
}
// canReach(heights, r, c, isPacific): BFS/DFS from (r, c) following the flow rule,
// checking whether the border for the given ocean is ever reached. Omitted for brevity.`,
        complexity: { time: 'O((rows · cols)²)', space: 'O(rows · cols)' },
        whenToUse: "Only useful to confirm the flow rule out loud — running a full grid search from every single cell is exactly the redundant work the reversed multi-source approach eliminates.",
      },
      {
        name: 'Approach 2 — Optimized: Reverse Multi-Source BFS From Both Oceans',
        why:
          "Instead of asking \"can this cell reach the ocean?\" from every cell, flip the question: start " +
          "simultaneously from EVERY Pacific-border cell at once, and follow the flow rule IN REVERSE (move to a " +
          "neighbor with height ≥ current, since that's what a real flow would have needed to come FROM). This " +
          "single combined search finds every Pacific-reachable cell in one pass. Do the same for Atlantic. The " +
          "intersection of both reachable sets is the answer.",
        steps: [
          'Start a multi-source BFS from ALL Pacific-border cells (top row + left column) simultaneously.',
          'Expand to a neighbor if its height is ≥ the current cell\'s height (the reverse of the flow rule) — mark it Pacific-reachable.',
          'Do the same multi-source BFS from ALL Atlantic-border cells (bottom row + right column), marking Atlantic-reachable cells.',
          'Any cell marked reachable by BOTH searches can flow to both oceans.',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: [1, 2, 3] },
            { label: 'row 1', values: [8, 9, 4] },
            { label: 'row 2', values: [7, 6, 5] },
          ],
          varNames: [],
          steps: [
            { highlights: { 'row 0': [0, 1, 2], 'row 1': [0], 'row 2': [0] }, note: 'Pacific sources (top row + left column): (0,0)=1, (0,1)=2, (0,2)=3, (1,0)=8, (2,0)=7. Expanding outward (to neighbors with height ≥ current) reaches every cell in this small grid.' },
            { highlights: { 'row 2': [0, 1, 2], 'row 0': [2], 'row 1': [2] }, note: 'Atlantic sources (bottom row + right column): (2,0)=7, (2,1)=6, (2,2)=5, (0,2)=3, (1,2)=4. Expanding outward reaches (1,0)=8 and (1,1)=9 as well — but NOT (0,0)=1 or (0,1)=2, since neither has a neighbor with height ≥ its own leading toward the Atlantic set.' },
            { note: 'Intersection: every cell except (0,0) and (0,1) reaches both oceans on this grid — 7 of the 9 cells.' },
          ],
        },
        code:
`List<int[]> pacificAtlantic(int[][] heights) {
    int rows = heights.length, cols = heights[0].length;
    boolean[][] pacific = new boolean[rows][cols];
    boolean[][] atlantic = new boolean[rows][cols];

    for (int c = 0; c < cols; c++) { bfs(heights, 0, c, pacific); bfs(heights, rows - 1, c, atlantic); }
    for (int r = 0; r < rows; r++) { bfs(heights, r, 0, pacific); bfs(heights, r, cols - 1, atlantic); }

    List<int[]> result = new ArrayList<>();
    for (int r = 0; r < rows; r++) {
        for (int c = 0; c < cols; c++) {
            if (pacific[r][c] && atlantic[r][c]) result.add(new int[] { r, c });
        }
    }
    return result;
}

private void bfs(int[][] heights, int startR, int startC, boolean[][] reachable) {
    if (reachable[startR][startC]) return;
    Queue<int[]> queue = new LinkedList<>();
    queue.add(new int[] { startR, startC });
    reachable[startR][startC] = true;
    int[][] dirs = { {1,0}, {-1,0}, {0,1}, {0,-1} };
    while (!queue.isEmpty()) {
        int[] cell = queue.poll();
        for (int[] d : dirs) {
            int nr = cell[0] + d[0], nc = cell[1] + d[1];
            if (nr >= 0 && nr < heights.length && nc >= 0 && nc < heights[0].length
                    && !reachable[nr][nc] && heights[nr][nc] >= heights[cell[0]][cell[1]]) {
                reachable[nr][nc] = true;
                queue.add(new int[] { nr, nc });
            }
        }
    }
}`,
        complexity: { time: 'O(rows · cols)', space: 'O(rows · cols)' },
        interviewNote: "Reversing the direction of a search to go FROM the destination(s) backward is a recurring trick worth recognizing — it turns \"check every source individually\" into one combined pass per destination.",
        whenToUse: "This reverse multi-source BFS technique is the standard answer any time a problem asks \"which cells/nodes can reach ALL of several distinct targets\" — checking from every candidate individually is almost always the slower brute-force alternative.",
      },
    ],
  },
  {
    id: 'rotting-oranges',
    title: 'Rotting Oranges',
    difficulty: 'Medium',
    leetcode: { number: 994, url: 'https://leetcode.com/problems/rotting-oranges/' },
    statement:
      'A grid contains 0 (empty), 1 (fresh orange), or 2 (rotten orange). Every minute, a rotten orange rots ' +
      'every fresh orange adjacent to it (up/down/left/right). Return the minutes until no fresh oranges remain, ' +
      'or -1 if some fresh orange can never rot.',
    example: {
      input: 'grid = [[2,1,1],[1,1,0],[0,1,1]]',
      output: '4',
      note: 'The rot spreads outward minute by minute from the single starting rotten orange.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Rescan the Whole Grid Every Minute)',
        why:
          "Simulate minute by minute: on each minute, scan the ENTIRE grid, rotting any fresh orange adjacent to " +
          "an already-rotten one, and repeat until a full pass makes no changes. Correct, but every single " +
          "minute re-examines every cell, even ones nowhere near the current rot frontier.",
        steps: [
          'Repeat: scan the whole grid, rotting any fresh orange adjacent to a rotten one (using yesterday\'s rotten cells, not today\'s new ones, to keep minutes accurate).',
          'Track whether this pass changed anything.',
          'Stop when a full pass produces no new rotten oranges.',
          'Every minute costs a full grid scan, regardless of how small the actual "rot frontier" is.',
        ],
        code:
`int orangesRottingBruteForce(int[][] grid) {
    int minutes = 0;
    while (hasFreshOranges(grid)) {
        boolean[][] newlyRotten = new boolean[grid.length][grid[0].length];
        boolean changed = false;
        for (int r = 0; r < grid.length; r++) {
            for (int c = 0; c < grid[0].length; c++) {
                if (grid[r][c] == 1 && adjacentToRotten(grid, r, c)) {
                    newlyRotten[r][c] = true;
                    changed = true;
                }
            }
        }
        if (!changed) return -1;   // stuck — some fresh orange is unreachable
        for (int r = 0; r < grid.length; r++)
            for (int c = 0; c < grid[0].length; c++)
                if (newlyRotten[r][c]) grid[r][c] = 2;
        minutes++;
    }
    return minutes;
}
// hasFreshOranges(...) and adjacentToRotten(...) omitted for brevity.`,
        complexity: { time: 'O((rows · cols)²) worst case', space: 'O(rows · cols)' },
        whenToUse: "Only useful to confirm the rotting rule out loud — rescanning the entire grid every minute redoes far more work than tracking just the active rot frontier.",
      },
      {
        name: 'Approach 2 — Optimized: Multi-Source BFS',
        why:
          "Start a BFS with ALL initially-rotten oranges in the queue at once, and process it LEVEL BY LEVEL — " +
          "each full level processed corresponds to exactly one minute passing, since every orange at the same " +
          "BFS depth rots simultaneously.",
        steps: [
          'Enqueue every initially-rotten orange, and count the fresh oranges.',
          'Process the queue one full level at a time — everything currently in the queue represents "this minute\'s" rotten oranges.',
          'For each one, rot any adjacent fresh orange, decrement the fresh count, and enqueue it for the NEXT level.',
          'After each full level, increment the minute counter. If fresh oranges remain once the queue empties, return -1.',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: [2, 1, 1] },
            { label: 'row 1', values: [1, 1, 0] },
            { label: 'row 2', values: [0, 1, 1] },
          ],
          varNames: ['fresh', 'minutes'],
          steps: [
            { highlights: { 'row 0': [0] }, vars: { fresh: 6, minutes: 0 }, note: 'Minute 0: queue = [(0,0)]. 6 fresh oranges to go.' },
            { snapshots: { 'row 0': [2, 2, 1], 'row 1': [2, 1, 0] }, highlights: { 'row 0': [1], 'row 1': [0] }, vars: { fresh: 4, minutes: 1 }, note: 'Minute 1: (0,0)\'s neighbors (0,1) and (1,0) rot. Fresh remaining: 4.' },
            { snapshots: { 'row 0': [2, 2, 2], 'row 1': [2, 2, 0] }, highlights: { 'row 0': [2], 'row 1': [1] }, vars: { fresh: 2, minutes: 2 }, note: 'Minute 2: (0,1)\'s and (1,0)\'s fresh neighbors — (0,2) and (1,1) — rot. Fresh remaining: 2.' },
            { snapshots: { 'row 2': [0, 2, 1] }, highlights: { 'row 2': [1] }, vars: { fresh: 1, minutes: 3 }, note: 'Minute 3: (1,1)\'s fresh neighbor (2,1) rots. Fresh remaining: 1.' },
            { snapshots: { 'row 2': [0, 2, 2] }, highlights: { 'row 2': [2] }, vars: { fresh: 0, minutes: 4 }, note: 'Minute 4: (2,1)\'s fresh neighbor (2,2) rots. Fresh remaining: 0 — done. Final answer: 4 minutes.' },
          ],
        },
        code:
`int orangesRotting(int[][] grid) {
    int rows = grid.length, cols = grid[0].length;
    Queue<int[]> queue = new LinkedList<>();
    int fresh = 0;
    for (int r = 0; r < rows; r++) {
        for (int c = 0; c < cols; c++) {
            if (grid[r][c] == 2) queue.add(new int[] { r, c });
            else if (grid[r][c] == 1) fresh++;
        }
    }

    int minutes = 0;
    int[][] dirs = { {1,0}, {-1,0}, {0,1}, {0,-1} };
    while (!queue.isEmpty() && fresh > 0) {
        int levelSize = queue.size();
        for (int i = 0; i < levelSize; i++) {
            int[] cell = queue.poll();
            for (int[] d : dirs) {
                int nr = cell[0] + d[0], nc = cell[1] + d[1];
                if (nr >= 0 && nr < rows && nc >= 0 && nc < cols && grid[nr][nc] == 1) {
                    grid[nr][nc] = 2;
                    fresh--;
                    queue.add(new int[] { nr, nc });
                }
            }
        }
        minutes++;
    }
    return fresh == 0 ? minutes : -1;
}`,
        complexity: { time: 'O(rows · cols)', space: 'O(rows · cols) — the queue, worst case' },
        interviewNote: "This is the exact same \"queue size = current level\" technique from Binary Tree Level Order Traversal, applied to a grid instead of a tree — each BFS level corresponds cleanly to one unit of elapsed time.",
        whenToUse: "Multi-source BFS (starting the queue with several sources at once, instead of one) is the standard technique whenever multiple starting points spread outward simultaneously and in lockstep.",
      },
    ],
  },
  {
    id: 'redundant-connection',
    title: 'Redundant Connection',
    difficulty: 'Medium',
    leetcode: { number: 684, url: 'https://leetcode.com/problems/redundant-connection/' },
    statement:
      'A tree with n nodes had one extra edge added, turning it into a graph with exactly one cycle. Given the ' +
      'edges in the order they were added, return the edge that can be removed to restore a valid tree. If ' +
      'multiple answers exist, return the one that occurs LAST in the input.',
    example: {
      input: 'edges = [[1,2], [1,3], [2,3]]',
      output: '[2, 3]',
      note: 'Edges [1,2] and [1,3] already connect all 3 nodes into a tree — [2,3] is the extra edge that creates the cycle.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Remove Each Edge, Check for a Valid Tree)',
        why:
          "For each edge, temporarily remove it and check whether the remaining graph is still fully connected " +
          "(a valid tree needs exactly n−1 edges connecting all n nodes with no cycle). Correct, but every edge " +
          "removal triggers its own full connectivity check.",
        steps: [
          'For each edge in the input, temporarily remove it.',
          'Run a BFS/DFS to check whether the remaining graph is still fully connected (a valid tree).',
          'If it is, this removed edge is a valid answer — track the one that appears latest in the input.',
          'Every candidate edge triggers a full O(n) connectivity check — O(n) edges × O(n) check = O(n²).',
        ],
        code:
`int[] findRedundantConnectionBruteForce(int[][] edges) {
    for (int i = edges.length - 1; i >= 0; i--) {
        if (formsValidTreeWithout(edges, i)) return edges[i];
    }
    return new int[] {};
}
// formsValidTreeWithout(edges, skipIndex): builds the graph excluding edges[skipIndex],
// then BFS/DFS-checks full connectivity across all nodes. Omitted for brevity.`,
        complexity: { time: 'O(n²)', space: 'O(n)' },
        whenToUse: "Only useful to confirm what \"restores a valid tree\" means — checking every edge removal independently repeats a huge amount of connectivity-checking work.",
      },
      {
        name: 'Approach 2 — Optimized: Union-Find, First Edge That Closes a Cycle',
        why:
          "Process edges in the given order, union-ing their two endpoints. The FIRST edge whose two endpoints " +
          "are ALREADY in the same component (found via Union-Find) is provably the answer: every edge before " +
          "it was needed to connect the tree, and this is the earliest point a redundant connection appears — " +
          "which, read in input order, is also guaranteed to be the last such edge overall for this problem\'s constraints.",
        steps: [
          'Initialize every node as its own separate component.',
          'Process edges in order: for each, find the root of both endpoints.',
          'If the roots are already the same, this edge connects two already-connected nodes — return it immediately.',
          'Otherwise, union the two components and continue to the next edge.',
        ],
        visual: {
          graph: { label: 'edges processed in order', nodes: [1, 2, 3], edges: [[1, 2], [1, 3], [2, 3]], directed: false },
          varNames: [],
          steps: [
            { highlights: { graph: [1, 2] }, highlightEdges: [[1, 2]], note: 'Edge [1,2]: find(1)=1, find(2)=2 — different roots. Union: 2 joins 1\'s component.' },
            { highlights: { graph: [1, 3] }, highlightEdges: [[1, 3]], note: 'Edge [1,3]: find(1)=1, find(3)=3 — different roots. Union: 3 joins 1\'s component. All 3 nodes now connected.' },
            { highlights: { graph: [2, 3] }, highlightEdges: [[2, 3]], note: 'Edge [2,3]: find(2)=1 (via the earlier union), find(3)=1 — SAME root already! This edge is redundant. Return [2, 3].' },
          ],
        },
        code:
`int[] findRedundantConnection(int[][] edges) {
    int n = edges.length;
    int[] parent = new int[n + 1];
    for (int i = 1; i <= n; i++) parent[i] = i;

    for (int[] edge : edges) {
        int rootA = find(parent, edge[0]);
        int rootB = find(parent, edge[1]);
        if (rootA == rootB) return edge;   // already connected — this is the redundant edge
        parent[rootA] = rootB;
    }
    return new int[] {};
}

private int find(int[] parent, int x) {
    if (parent[x] != x) {
        parent[x] = find(parent, parent[x]);   // path compression
    }
    return parent[x];
}`,
        complexity: { time: 'O(n · α(n)) — effectively O(n)', space: 'O(n)' },
        interviewNote: "This builds directly on the Union-Find cycle-detection drill from this pattern's beginner problems — the only new idea here is that the FIRST cycle-closing edge found (processed in input order) is exactly the one the problem wants returned.",
        whenToUse: "Whenever a problem describes edges being added one at a time and asks which one first creates a cycle (or breaks a tree property), Union-Find processed in that same order is the standard, efficient answer.",
      },
    ],
  },
];

export default graphProblems;
